import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Shield, Monitor, Clock, Mail, Loader2 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { applyAuthToken, LOGIN_NOTICE_KEY } from "../lib/authToken";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [otpInput, setOtpInput] = useState("");
  // Tracks that a code was requested for this address (5-min UI hint). The
  // code itself lives server-side — the browser never sees it in production.
  const [emailedOtp, setEmailedOtp] = useState(null); // { email, expiresAt }
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timeInterval);
  }, []);

  // Explain an involuntary bounce (expired session mid-action) once.
  useEffect(() => {
    const notice = sessionStorage.getItem(LOGIN_NOTICE_KEY);
    if (!notice) return;
    sessionStorage.removeItem(LOGIN_NOTICE_KEY);
    toast.error(notice, { duration: 8000 });
  }, []);

  // Auto-expire the "code sent" hint after its TTL elapses
  useEffect(() => {
    if (!emailedOtp) return;
    const ms = emailedOtp.expiresAt - Date.now();
    if (ms <= 0) {
      setEmailedOtp(null);
      setEmailSent(false);
      return;
    }
    const t = setTimeout(() => {
      setEmailedOtp(null);
      setEmailSent(false);
    }, ms);
    return () => clearTimeout(t);
  }, [emailedOtp]);

  // Typing a different address invalidates the previous hint
  useEffect(() => {
    if (emailedOtp && emailedOtp.email !== email.trim().toLowerCase()) {
      setEmailedOtp(null);
      setEmailSent(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const looksLikeEmail = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email.trim());

  const sendOtpEmail = async () => {
    if (!looksLikeEmail) {
      toast.error("Enter your work email address");
      return;
    }

    setSendingEmail(true);
    try {
      // The server decides whether the address belongs to an account, and
      // tells us nothing either way.
      const res = await axios.post(`${API}/auth/staff/request-otp`, {
        email: email.trim().toLowerCase(),
      });
      setEmailedOtp({
        email: email.trim().toLowerCase(),
        expiresAt: Date.now() + 5 * 60 * 1000,
      });
      setEmailSent(true);
      if (res.data?.dev_mode && res.data?.otp) {
        // Preview/dev only: codes are never emailed there, so show it here.
        setOtpInput(res.data.otp);
        toast.success(`Preview login code: ${res.data.otp}`, { duration: 20000 });
      } else {
        toast.success(res.data?.message || "If that address has an account, a code is on its way");
      }
    } catch (error) {
      const serverMsg = error?.response?.data?.detail;
      toast.error(serverMsg || "Failed to send code. Please try again.");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleLogin = async () => {
    if (!looksLikeEmail) {
      toast.error("Enter your work email address");
      return;
    }
    if (otpInput.length !== 6) {
      toast.error("Enter your 6-digit code");
      return;
    }

    setLoading(true);
    try {
      // Server validates the OTP and issues a signed JWT session token.
      const res = await axios.post(`${API}/auth/staff/verify-otp`, {
        email: email.trim().toLowerCase(),
        otp: otpInput,
      });

      const { token, session: serverSession } = res.data;
      const role = serverSession?.role || "agent";
      const isManager = role === "manager" || role === "admin";

      // Store the JWT and attach it to all future axios requests.
      applyAuthToken(token);

      const session = {
        ...serverSession,
        loginTime: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(), // 8 hours
      };
      localStorage.setItem("equipr_session", JSON.stringify(session));

      toast.success(`Welcome, ${session.name}!`);

      setTimeout(() => {
        if (isManager) {
          navigate("/");
        } else {
          navigate("/agent");
        }
      }, 500);
    } catch (error) {
      console.error("Login failed:", error);
      const serverMsg = error?.response?.data?.detail;
      toast.error(serverMsg || "Invalid or expired code. Please try again.");
      setLoading(false);
    }
  };

  const handleManagerAccess = () => {
    // Direct manager access to command centre
    navigate("/command-centre");
  };

  return (
    <div className="min-h-screen bg-[#EDEAE3] flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              EQUIPR INTELLIGENCE
            </h1>
            <p className="text-[#59554D]">Enter your work email and we'll send you a login code</p>
          </div>

          <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
            <CardContent className="p-6 space-y-6">
              {/* Email */}
              <div className="space-y-2">
                <label className="text-sm text-[#59554D]">Work email address</label>
                <Input
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !emailSent) sendOtpEmail(); }}
                  placeholder="you@equipr.software"
                  className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                  data-testid="email-input"
                />
              </div>

              {/* OTP Input */}
              <div className="space-y-2">
                <label className="text-sm text-[#59554D]">Enter Your Code</label>
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={otpInput}
                  onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => { if (e.key === "Enter") handleLogin(); }}
                  placeholder="6-digit code"
                  className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] text-center text-2xl tracking-widest font-mono"
                  data-testid="otp-input"
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-[#59554D]">
                    {emailSent ? "✓ If that address has an account, the code is on its way" : "Click to receive your code"}
                  </p>
                  <Button
                    size="sm"
                    variant={emailSent ? "ghost" : "default"}
                    onClick={sendOtpEmail}
                    disabled={sendingEmail || !looksLikeEmail}
                    className={emailSent 
                      ? "text-[#0E6B3F] hover:bg-[#E3F3E9] h-7 px-2 text-xs"
                      : "bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A] h-7 px-3 text-xs"
                    }
                    data-testid="email-otp-btn"
                  >
                    {sendingEmail ? (
                      <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <Mail className="w-3 h-3 mr-1" />
                    )}
                    {emailSent ? "Resend" : "Email Code"}
                  </Button>
                </div>
              </div>

              {/* Login Button */}
              <Button
                onClick={handleLogin}
                disabled={loading || !looksLikeEmail || otpInput.length !== 6}
                className="w-full bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A]"
                data-testid="login-btn"
              >
                {loading ? "Logging in..." : "Login"}
              </Button>

              {/* Manager Quick Access */}
              <div className="pt-4 border-t border-[#DAD5CA]">
                <Button
                  variant="outline"
                  onClick={handleManagerAccess}
                  className="w-full border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#59554D]"
                  data-testid="command-centre-direct"
                >
                  <Monitor className="w-4 h-4 mr-2" />
                  Command Centre (TV Display)
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Side - Info Panel (desktop only; it would overflow a phone) */}
      <div className="hidden lg:flex w-96 bg-[#FFFFFF] border-l border-[#DAD5CA] p-8 flex-col">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 text-[#59554D] mb-2">
            <Clock className="w-4 h-4" />
            <span className="font-mono">
              {currentTime.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          </div>
          <h2 className="text-lg font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            SECURE LOGIN
          </h2>
          <p className="text-xs text-[#59554D]">Email-based authentication</p>
        </div>

        <div className="flex-1 flex flex-col justify-center">
          <div className="bg-[#F4F2ED] rounded-lg p-6 text-center">
            <Mail className="w-12 h-12 mx-auto text-[#8A6D00] mb-4" />
            <h3 className="text-[#1A1A1A] font-medium mb-2">How to Login</h3>
            <ol className="text-sm text-[#59554D] text-left space-y-3">
              <li className="flex items-start gap-2">
                <span className="bg-[#FFD108] text-[#1A1A1A] rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">1</span>
                <span>Enter your EQUIPR work email address</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-[#FFD108] text-[#1A1A1A] rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">2</span>
                <span>Click "Email Code" — if the address has an account, a code is sent</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-[#FFD108] text-[#1A1A1A] rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">3</span>
                <span>Enter the 6-digit code from your email</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-[#FFD108] text-[#1A1A1A] rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">4</span>
                <span>Click "Login" to access your dashboard</span>
              </li>
            </ol>
          </div>
          
          <div className="mt-6 p-4 bg-[#FFF7D1] rounded-lg border border-[#E8D48A]">
            <p className="text-xs text-[#6B5600] text-center">
              <Shield className="w-3 h-3 inline mr-1" />
              Emailed codes are valid for 5 minutes
            </p>
          </div>
        </div>

        <div className="mt-auto pt-4 text-center">
          <p className="text-xs text-[#59554D]">
            Session expires after 8 hours
          </p>
          <a 
            href="/client" 
            className="text-xs text-[#8A6D00] hover:underline mt-2 inline-block"
            data-testid="client-portal-link"
          >
            Client Portal Access →
          </a>
        </div>
      </div>
    </div>
  );
}
